import { useCallback, useEffect, useState } from 'react';
import { pbAuthedFetch } from "../authHeaders";

// Recurring delivery.

interface Schedule {
  id: string;
  name: string;
  cadence: string;
  format: string;
  recipients: string[];
  nextRunAt: string;
  lastStatus?: string;
  lastError?: string;
}

interface ReportSchedulesProps {
  cadences: string[];
  currentConfig: () => Record<string, unknown> | null;
  format: string;
  showToast: (msg: string) => void;
}

const when = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return String(iso || '');
  }
};

export function ReportSchedules({
  cadences,
  currentConfig,
  format,
  showToast,
}: ReportSchedulesProps) {
  const [rows, setRows] = useState<Schedule[] | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [name, setName] = useState('');
  const [cadence, setCadence] = useState('monthly');
  const [recipients, setRecipients] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    pbAuthedFetch('/api/reports/schedules')
      .then((res) => (res && res.ok ? res.json() : null))
      .then((body) => {
        setRows((body && body.schedules) || []);
        if (body && typeof body.enabled === 'boolean') setEnabled(body.enabled);
      })
      .catch(() => setRows([]));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = useCallback(async () => {
    setBusy(true);
    try {
      const res = await pbAuthedFetch('/api/reports/schedules', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          cadence,
          format,
          recipients,
          config: currentConfig(),
        }),
      });
      const payload = res ? await res.json().catch(() => ({})) : {};
      if (!res || !res.ok) throw new Error(payload.error || 'Could not save the schedule');
      setName('');
      setRecipients('');
      load();
      showToast(`"${payload.schedule.name}" scheduled ${payload.schedule.cadence}`);
    } catch (err) {
      showToast((err as Error).message);
    } finally {
      setBusy(false);
    }
  }, [name, cadence, format, recipients, currentConfig, load, showToast]);

  const remove = useCallback(
    async (id: string) => {
      const res = await pbAuthedFetch(`/api/reports/schedules/${id}`, { method: 'DELETE' });
      if (res && res.ok) load();
      else showToast('Could not delete the schedule');
    },
    [load, showToast]
  );

  return (
    <div className="rpt-schedules">
      {!enabled && (
        <p className="rpt-hint" role="status">
          Delivery is switched off in this environment — schedules are saved but nothing is sent.
        </p>
      )}

      <label className="rpt-field">
        <span className="rpt-label">Schedule name</span>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={80}
        />
      </label>

      <label className="rpt-field">
        <span className="rpt-label">Cadence</span>
        <select value={cadence} onChange={(e) => setCadence(e.target.value)}>
          {(cadences || ['weekly', 'monthly', 'quarterly']).map((c) => (
            <option key={c} value={c}>
              {c.charAt(0).toUpperCase() + c.slice(1)}
            </option>
          ))}
        </select>
      </label>

      <label className="rpt-field">
        <span className="rpt-label">Recipients</span>
        <input
          type="text"
          value={recipients}
          onChange={(e) => setRecipients(e.target.value)}
          placeholder="cor@agency.gov, co@agency.gov"
        />
      </label>
      <p className="rpt-hint">
        Recipients get an expiring, revocable link — never the document as an attachment. The report
        is generated as you, and re-checks your access each time it runs.
      </p>

      <button
        type="button"
        className="btn btn--primary btn--sm"
        disabled={busy || !name.trim() || !recipients.trim()}
        aria-busy={busy}
        onClick={create}
      >
        {busy ? 'Saving…' : 'Schedule this report'}
      </button>

      {rows && rows.length === 0 && (
        <div className="state">
          <p className="state__body">
            Nothing scheduled yet. A schedule generates this report on its own and mails a link.
          </p>
        </div>
      )}

      {rows && rows.length > 0 && (
        <ul className="rpt-runs rpt-schedlist">
          {rows.map((s) => (
            <li key={s.id}>
              <div className="rpt-run-main">
                <span className="rpt-run-t">{s.name}</span>
                <span className="rpt-run-m">
                  {s.cadence} · {String(s.format).toUpperCase()} · next {when(s.nextRunAt)}
                </span>
                <span className="rpt-run-m">{s.recipients.join(', ')}</span>
                {s.lastStatus && (
                  <span className="rpt-run-m">
                    Last run: {s.lastStatus}
                    {s.lastError ? ` — ${s.lastError}` : ''}
                  </span>
                )}
              </div>
              <div className="rpt-run-act">
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => remove(s.id)}
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
